import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  SlidersHorizontal,
  ShieldCheck,
  FileSignature,
  Handshake,
  LineChart,
  Layers,
} from "lucide-react";
import { Reveal, Overline } from "@/components/Primitives";

const REASONS = [
  {
    num: "01",
    icon: SlidersHorizontal,
    title: "Flexible pricing structure",
    body: "Engagements scoped to your stage and cash flow — hourly, fixed-fee, or fractional retainer. No rigid packages, no surprises. You pay for the finance function you actually need.",
  },
  {
    num: "02",
    icon: ShieldCheck,
    title: "Data security",
    body: "Encrypted file transfer, access-controlled workpapers, and secure remote operations. Your financial data is handled with bank-grade discipline at every step.",
  },
  {
    num: "03",
    icon: FileSignature,
    title: "NDA set up",
    body: "Confidentiality first. We execute a mutual NDA before any data changes hands, so sensitive financials and business context stay protected from the very first conversation.",
  },
  {
    num: "04",
    icon: Handshake,
    title: "Trust factor",
    body: "Long-term relationships built on transparency and accountability. You work directly with senior advisors who stand behind their numbers and communicate proactively.",
  },
  {
    num: "05",
    icon: LineChart,
    title: "CFO-level advisory, added",
    body: "Beyond the books — strategic finance guidance on forecasting, cash flow, capital strategy, and board reporting. Real CFO horsepower, without the full-time overhead.",
  },
  {
    num: "06",
    icon: Layers,
    title: "Deep tech-stack experience",
    body: "Fluent across the modern finance stack — QuickBooks Online, Bill.com, and Sage Intacct, plus the tools that surround them. We meet you on the systems you already run.",
  },
];

const STACK = [
  "QuickBooks Online (QBO)",
  "Bill.com",
  "Sage Intacct",
  "NetSuite",
  "Xero",
  "Ramp",
  "Expensify",
  "Gusto",
];

export default function WhyBridge() {
  return (
    <>
      <section className="bg-obsidian text-bone">
        <div className="mx-auto max-w-[1400px] px-6 pb-20 pt-40 lg:px-12 lg:pb-28 lg:pt-52">
          <Reveal>
            <Overline dark>Why Bridge Advisors</Overline>
            <h1 className="mt-8 max-w-4xl font-serif text-5xl leading-[0.95] tracking-tight md:text-7xl">
              Reasons finance leaders <span className="italic text-bone/70">choose us.</span>
            </h1>
            <p className="mt-8 max-w-xl text-bone/70 md:text-lg">
              Flexible, secure, and senior by default. Here is what sets Bridge
              Advisors apart for US organizations.
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid="reasons-grid" className="mx-auto max-w-[1400px] px-6 lg:px-12">
        <div className="grid grid-cols-1 border-l border-t border-border md:grid-cols-2 lg:grid-cols-3">
          {REASONS.map((r, i) => {
            const Icon = r.icon;
            return (
              <Reveal key={r.num} delay={(i % 3) * 0.07}>
                <div
                  data-testid={`reason-card-${r.num}`}
                  className="group flex h-full flex-col border-b border-r border-border p-8 transition-colors duration-500 hover:bg-obsidian hover:text-bone lg:p-10"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs text-brand transition-colors group-hover:text-bone/60">[ {r.num} ]</span>
                    <Icon size={22} strokeWidth={1.4} className="text-brand transition-colors group-hover:text-bone" />
                  </div>
                  <h3 className="mt-16 font-serif text-2xl text-obsidian transition-colors group-hover:text-bone">{r.title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-stone transition-colors group-hover:text-bone/70">{r.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section data-testid="tech-stack" className="mx-auto max-w-[1400px] px-6 py-24 lg:px-12 lg:py-36">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
          <Reveal className="md:col-span-4">
            <Overline>Tech stack</Overline>
            <h2 className="mt-8 font-serif text-4xl leading-tight tracking-tight text-obsidian md:text-5xl">
              Fluent in the tools you <span className="italic">already run.</span>
            </h2>
            <p className="mt-6 max-w-sm text-stone">
              Onboarding is faster and cleaner when your advisors already know your
              systems inside out.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-8 md:pl-8">
            <div className="grid grid-cols-1 border-l border-t border-border sm:grid-cols-2">
              {STACK.map((tool) => (
                <div
                  key={tool}
                  data-testid={`stack-${tool.split(" ")[0].toLowerCase()}`}
                  className="border-b border-r border-border px-6 py-7 font-serif text-xl text-obsidian"
                >
                  {tool}
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 pb-24 lg:px-12 lg:pb-36">
        <Reveal className="border border-border bg-sage p-10 md:p-20">
          <div className="grid grid-cols-1 items-center gap-10 md:grid-cols-12">
            <div className="md:col-span-8">
              <Overline>Let's talk</Overline>
              <h2 className="mt-8 font-serif text-4xl leading-tight tracking-tight text-obsidian md:text-5xl">
                See how we'd fit your finance function.
              </h2>
            </div>
            <div className="md:col-span-4 md:text-right">
              <Link
                to="/contact"
                data-testid="why-cta"
                className="group inline-flex items-center gap-2 bg-obsidian px-8 py-4 text-sm font-medium text-bone transition-colors duration-300 hover:bg-brand"
              >
                Book a Consultation
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </>
  );
}
